import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const CATEGORY_MAP = { equipos: "physical", software: "software", consultoria: "consulting" };

export default function Catalog({ fixedCategory }) {
  const params = useParams();
  const { t } = useLang();
  const routeCat = fixedCategory || CATEGORY_MAP[params.cat];
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState(routeCat || "all");
  const [query, setQuery] = useState("");
  const [maxPrice, setMaxPrice] = useState(0);
  const [brand, setBrand] = useState("all");
  const [avail, setAvail] = useState("all");

  useEffect(() => { setFilter(routeCat || "all"); }, [routeCat]);

  useEffect(() => {
    const p = filter === "all" ? {} : { category: filter };
    api.get("/products", { params: p }).then(({ data }) => setProducts(data));
    setBrand("all"); setAvail("all");
  }, [filter]);

  const ceiling = useMemo(() => products.reduce((m, p) => Math.max(m, p.price), 0), [products]);
  useEffect(() => { setMaxPrice(ceiling); }, [ceiling]);

  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))].sort(), [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) =>
      (!q || p.name.toLowerCase().includes(q) || (p.tagline || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q)) &&
      (maxPrice === 0 || p.price <= maxPrice) &&
      (brand === "all" || p.brand === brand) &&
      (avail === "all" || (p.availability || "in_stock") === avail)
    );
  }, [products, query, maxPrice, brand, avail]);

  const headerKey = ["physical", "software", "consulting"].includes(filter) ? filter : "all";
  const selCls = "border border-ink/15 bg-[color:var(--bg)] px-4 py-2.5 outline-none focus:border-gold transition-colors text-sm cursor-pointer";

  return (
    <div className="pt-32 md:pt-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-12" data-testid="catalog-header">
          <p className="overline text-gold mb-5">{t("catalog.overline")}</p>
          <h1 className="font-display font-extrabold tracking-tighter text-[clamp(2.4rem,6vw,5rem)] leading-[0.92]">{t(`catalog.${headerKey}.title`)}</h1>
          <p className="text-ink/55 text-lg mt-5 max-w-xl">{t(`catalog.${headerKey}.sub`)}</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {["all", "physical", "software", "consulting"].map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
                className={`px-5 py-2 text-sm font-medium border transition-colors duration-300 ${filter === c ? "bg-blue text-[color:var(--bg)] border-blue" : "border-ink/15 hover:border-gold hover:text-gold"}`}>
                {c === "all" ? t("catalog.filterAll") : t(`cat.${c}`)}
              </button>
            ))}
          </div>
          <div className="lg:ml-auto relative w-full lg:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("catalog.search")} data-testid="search-input"
              className="w-full border border-ink/15 bg-[color:var(--bg)] pl-9 pr-4 py-2.5 outline-none focus:border-gold transition-colors text-sm" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          {brands.length > 0 && (
            <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="brand-filter" className={selCls}>
              <option value="all">{t("catalog.allBrands")}</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
          <select value={avail} onChange={(e) => setAvail(e.target.value)} data-testid="availability-filter" className={selCls}>
            <option value="all">{t("catalog.availability")}: {t("catalog.allAvail")}</option>
            <option value="in_stock">{t("avail.in_stock")}</option>
            <option value="on_demand">{t("avail.on_demand")}</option>
          </select>
        </div>

        {ceiling > 0 && (
          <div className="flex items-center gap-4 mb-10 pb-6 border-b border-ink/10">
            <span className="text-sm text-ink/50 font-mono whitespace-nowrap">{t("catalog.price")}: ≤ ${maxPrice.toLocaleString()}</span>
            <input type="range" min="0" max={ceiling} step={Math.max(1, Math.round(ceiling / 100))} value={maxPrice}
              onChange={(e) => setMaxPrice(Number(e.target.value))} data-testid="price-range"
              className="flex-1 max-w-md accent-[color:var(--gold)]" />
            <span className="text-sm text-ink/40 font-mono whitespace-nowrap" data-testid="results-count">{filtered.length} {t("catalog.results")}</span>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-32">
          {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          {filtered.length === 0 && <p className="text-ink/40 font-mono py-20">{t("catalog.none")}</p>}
        </div>
      </div>
    </div>
  );
}
